import PageLayoutModes from '../constants/PageLayoutModes';
import FieldTypes from '../constants/FieldTypes';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../constants/FieldTableColumnTypes';
import { compact } from './reactGridLayoutUtils';

/**
 * Format an array of react-grid-layout layouts from field positions
 *
 * @param {Array} fieldPositions
 * @returns {Array}
 */
export const getLayout = (fieldPositions) => {

  return fieldPositions.map((fieldPosition) => {

    //Convert joyfill position object to supported react-grid-layout object
    return {
      i: fieldPosition._id,
      x: fieldPosition.x,
      y: fieldPosition.y,
      w: fieldPosition.width,
      h: fieldPosition.height
    };

  });

};

/**
 * Calculate the number of PDF Pages (Letter 8 1/2 x 11) will be required to display all page fields.
 * Joyfill page fields are dynamic so they could snap many pages.
 *
 * @param {Array} measuredFieldPositions //field positions that have been properly measured
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @returns {Number} //Number of pages required
 *
 */
export const getPDFPageCountFromPage = (measuredFieldPositions, pageHeight, pageRowHeight) => {

  /**
   * Step 1: Find the field position layout that is last fieldPosition at the bottom of the form.
   */
  let lastFieldPosition = { y: 0, height: 0 };

  measuredFieldPositions.forEach((fieldPosition) => {
    if (fieldPosition.y > lastFieldPosition.y) lastFieldPosition = fieldPosition;
    if (fieldPosition.y === lastFieldPosition.y && fieldPosition.height > lastFieldPosition.height) lastFieldPosition = fieldPosition;
  });

  const bottom = (lastFieldPosition.y * pageRowHeight) + (lastFieldPosition.height * pageRowHeight);

  /**
   * Step 2: Determine how many page breaks this single page actually spans. Dynamic
   * fields can cause a single form page to span multiple page breaks.
   */
  const totalPDFPagesRequired = Math.ceil(bottom / pageHeight);

  return totalPDFPagesRequired;

};

/**
 * Handle the recalculation and update for the x, y properties of field positions.
 *
 * What does recalculation mean?
 *
 * We utilize the "compact" method from the react-grid-layout internals. This allows us
 * to properly recalculate y and height properties of fieldPositions to ensure there are
 * no overlapping or conflicting fieldPositions. For instance, see the example below:
 *
 * Before recalculation:
 * [
 *   { y: 1, height: 10 },
 *   { y: 2, height: 10 },
 * ]
 * after calcualtion
 * [
 *   { y: 1, height: 10 },
 *   { y: 11, height: 10 },
 * ]
 *
 * @param {Array} fieldPositions
 * @param {Number} pageCols
 * @param {String} pageLayout //grid or float
 * @returns {Array} //field positions after their y and height properties have been recalculated
 */
export const recalculateLayoutsForFieldPosition = (fieldPositions, pageCols, pageLayout) => {

  const originalLayouts = getLayout(fieldPositions);
  const nextLayouts = compact(originalLayouts, 'vertical', pageCols, pageLayout === PageLayoutModes.float);

  const layoutLookup = {};
  nextLayouts.forEach((layout) => layoutLookup[layout.i] = layout);

  const nextFieldPositions = [];
  fieldPositions.forEach((fieldPosition) => {

    const layout = layoutLookup[fieldPosition._id];

    nextFieldPositions.push({
      ...fieldPosition,
      x: layout.x,
      y: layout.y,
      height: layout.h,
      width: layout.w
    });

  });

  return nextFieldPositions;

};

/**
 * Get all field position layouts that conflict with the targeted page break index.
 *
 * @param {Array} layouts  //react-grid-layout onLayout handler generated layout objects after render
 * @param {Number} pageBreakTargetIndex //What page break point are we targeting
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @param {Object} pageRowHeight
 * @returns {Array} //field postions that conflict with a page break
 */
export const getFieldPositionsWithConflictsForTargetPageBreak = (fieldPositions, pageBreakTargetIndex, pageHeight, pageRowHeight, pageStyles) => {

  /**
   * Why do we have pageHeightReduction and why is it only for pageBreakTargetIndex 1?
   *
   * Page height reduction explanation: This is because of page styles like borderWidth, margin, padding, etc. change the starting
   * point of the top of fields. For instance we have page heights of 1056 and we have a page style
   * of margin 10 on the page. That means that the top most fieldPositions y coordinate doesn't start at 0,
   * it starts at 10 even though it's saved as fieldPosition.y === 0.
   *
   * Only the pageBreakTargetIndex 1 explanation: The top borderWidth, margin, padding, etc. only push
   * fields down on the very first page. Any field positions that get pushed to a second page don't
   * need to account for those styles because those styles don't apply to any pages other than the first
   * one in the page break list.
   */
  const pageMargin = pageStyles.margin || 0;
  const pagePadding = pageStyles.padding || 0;
  const pageBorderWidth = pageStyles.borderWidth || 0;
  const pageHeightReduction = pageBreakTargetIndex === 1 ? pageMargin + pagePadding + pageBorderWidth : 0;

  const targetPageBreakPointPixels = (pageBreakTargetIndex * pageHeight) - pageHeightReduction;

  const conflictingFieldPositions = [];

  fieldPositions.forEach((fieldPosition) => {

    const fieldPositionTopYPixels = (fieldPosition.y * pageRowHeight);
    const fieldPositionHeightPixels = (fieldPosition.height * pageRowHeight);
    const fieldPositionBottomYPixels = fieldPositionTopYPixels + fieldPositionHeightPixels;

    if (fieldPositionTopYPixels < targetPageBreakPointPixels && fieldPositionBottomYPixels > targetPageBreakPointPixels) {
      conflictingFieldPositions.push(fieldPosition);
    }

  });

  return conflictingFieldPositions;

};

/**
 * Adjust a field position so that it's not in the way of a page break.
 *
 * @param {Object} fieldPosition
 * @param {Number} pageBreakTargetIndex
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @param {Object} pageStyles //Page padding, margin and border width
 *
 * @returns {Object} //updated field positon
 */
export const adjustFieldPositionForPageBreakConflict = (fieldPosition, pageBreakTargetIndex, pageHeight, pageRowHeight, pageStyles) => {

  /**
   * Field positions that have a height greater than the page height will
   * force a page break through the field on the form.
   */
  if ((fieldPosition.height * pageRowHeight) > pageHeight) return fieldPosition;

  const pageMargin = pageStyles.margin;
  const pagePadding = pageStyles.padding;
  const pageBorderWidth = pageStyles.borderWidth;
  const pageHeightReduction = pageBreakTargetIndex === 1 ? pageMargin + pagePadding + pageBorderWidth : 0;

  const targetPageBreakPointPixels = (pageBreakTargetIndex * pageHeight) - pageHeightReduction;

  const fieldPositionTopYPixels = (fieldPosition.y * pageRowHeight);

  const topOverflowPixels = targetPageBreakPointPixels - fieldPositionTopYPixels;
  const topOverflowRows = Math.ceil(topOverflowPixels / pageRowHeight);

  const pageBreakPaddingPixels = 18;
  const pageBreakPaddingRows = Math.ceil(pageBreakPaddingPixels / pageRowHeight);

  //Used to adjust field height
  const nextHeight = fieldPosition.height + topOverflowRows + pageBreakPaddingRows;
  //Used to add white space above the field and push it below the page break line
  const internalTopPadding = (topOverflowRows + pageBreakPaddingRows) * pageRowHeight;

  /**
  * Ignore dynamic fields for now until we can do internal field adjusting in at
  * a future time.
  */
  if (fieldPosition.type === FieldTypes.table || fieldPosition.type === FieldTypes.chart) {
    return fieldPosition;
  }

  return {
    ...fieldPosition,
    height: nextHeight,
    internalTopPadding
  };

};

/**
 * Adjust the field positions to prevent page break issues.
 *
 * @param {Object} page
 * @returns {Array} //adjusted page field positions
 */
export const adjustFieldPostionsForPageBreaks = (page) => {

  const { fieldPositions, rowHeight, cols, height, padding, margin, borderWidth, layout } = page;

  const pageBreakCount = getPDFPageCountFromPage(fieldPositions, height, rowHeight) + 1;

  let adjustedFieldPositions = fieldPositions;
  const previouslyAdjustedFieldPositionLookup = {};

  for (let targetPageBreakIndex = 1; targetPageBreakIndex < pageBreakCount; targetPageBreakIndex++) {

    const conflictingPositions = getFieldPositionsWithConflictsForTargetPageBreak(adjustedFieldPositions, targetPageBreakIndex, height, rowHeight, { padding, margin, borderWidth });

    if (conflictingPositions.length > 0) {

      const resolvedFieldPositionLookup = {};
      conflictingPositions.forEach((fieldPositionConflict) => {

        /**
         * Only adjust fieldPositions that have not been previously adjusted.
         * What would cause a field to be adjusted twice? This would be caused by the fieldPosition
         * conflicting at multiple page break points. Usually only dynamic fields, like tables, grow
         * to intersect at multiple page break points.
         */
        if (!previouslyAdjustedFieldPositionLookup[fieldPositionConflict._id]) {
          previouslyAdjustedFieldPositionLookup[fieldPositionConflict._id] = true;
          resolvedFieldPositionLookup[fieldPositionConflict._id] = adjustFieldPositionForPageBreakConflict(fieldPositionConflict, targetPageBreakIndex, height, rowHeight, { padding, margin, borderWidth });
        }
      });

      adjustedFieldPositions = adjustedFieldPositions.map((fieldPosition) => {
        return resolvedFieldPositionLookup[fieldPosition._id] || fieldPosition;
      });

      adjustedFieldPositions = recalculateLayoutsForFieldPosition(adjustedFieldPositions, cols, layout);

    }

  }

  return adjustedFieldPositions;

};

/**
 * Get list of fields that should be measured by the PDF Export Page
 *
 * @param {Array} fields
 * @returns {Array};
 */
export const getMeasurableFields = (fields) => {
  return fields.filter((field) => {

    const { type, displayType, columnType } = field;

    if (type === FieldTypes.table && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.table && displayType === FieldDisplayTypes.inputGroup) return true;
    if (type === FieldTypes.table && columnType === FieldTableColumnTypes.image) return true;

    if (type === FieldTypes.chart && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.image && displayType === FieldDisplayTypes.original) return true;

    return false;

  });
};
